import { nextTick, readonly, ref } from "vue";
import { usePageScrollService } from "@allwyn/utils/composables/use-page-scroll-service";
import { HeaderAnalytics } from "./header-analytics";
import { useSmartBannerPosition } from "../smart-banner/smart-banner-position";

const { getPageScrollY } = usePageScrollService();

export interface HeaderState {
    active: boolean;
    transparent: boolean;
    locked: boolean;
    parallax: boolean;
    dark: boolean;
}

const headerState = ref<HeaderState>({
    active: false,
    transparent: true,
    locked: false,
    parallax: true,
    dark: false,
});

let lastPos = Math.max(getPageScrollY(), 0);
let headerHeight = 0;
let transparencyTimeout: number | null = null;
let showTimeout: number | null = null;
let isHeaderDisabled = false;
let mode: "show" | "parallax" | "hide" = "parallax";

const body = document.body;
const html = document.documentElement;
const header = document.querySelector<HTMLElement>("#header") as HTMLElement;
const sazkaNotification = document.querySelector<HTMLElement>(".sazka-notification");
const isHeaderActivePrivate = ref(false);
const isTransparencyEnabled = ref(true);
const isHeaderActive = readonly(isHeaderActivePrivate);

const PARALLAX_CLASS = "parallax";
const ACTIVE_CLASS = "active";
const DARK_CLASS = "dark";
const TRANSPARENT_CLASS = "transparent";
const ANIMATING_CLASS = "animating";
const BODY_TRANSPARENT_CLASS = "transparent-header";
const BODY_ACTIVE_CLASS = "header-active";
const SHOW_DELAY = 500;

interface HeaderClasses {
    parallax?: boolean;
    transparent?: boolean;
    active?: boolean;
    animating?: boolean;
    dark?: boolean;
}

/**
 * Options for the header
 *
 * - active: Show / hide the header
 * - transparent: Make the header transparent
 * - animated: Animate the header transitions (show / hide, transparency)
 * - locked: Lock the header state (remove scroll listener)
 * - parallax: True sets the position to static (false for fixed)
 */
export interface HeaderOptions {
    /**
     * Show / hide the header
     */
    active?: boolean;
    /**
     * Make the header transparent
     */
    transparent?: boolean;
    /**
     * Animate the header transitions (show / hide, transparency)
     */
    animated?: boolean;
    /**
     * Lock the header state (remove scroll listener)
     */
    locked?: boolean;
    /**
     * True sets the position to static (false for fixed)
     */
    parallax?: boolean;
    /**
     * Sets dark mode for the header
     */
    dark?: boolean;
}

export const useHeader = () => ({
    isHeaderActive,
    headerState: readonly(headerState),
    useHeaderScrollFeatures,
    getHeaderState,
    setHeaderState,
    setHeaderTransparency,
    disableHeader,
    enableHeader,
    resetHeader,
});

const headerClassMap: Record<keyof HeaderClasses, string> = {
    parallax: PARALLAX_CLASS,
    transparent: TRANSPARENT_CLASS,
    active: ACTIVE_CLASS,
    animating: ANIMATING_CLASS,
    dark: DARK_CLASS,
};

function hasClass(element: HTMLElement, className: string) {
    if (!element) return false;

    return element?.classList.contains(className);
}

function addClass(element: HTMLElement | null, className: string | string[]) {
    if (!element) return;

    if (Array.isArray(className)) {
        className.forEach((c) => element?.classList.add(c));
    } else {
        element?.classList.add(className);
    }
}

function setHeaderClasses(options: HeaderClasses) {
    // value can be true | false | undefined
    for (const [key, value] of Object.entries(options) as [keyof HeaderClasses, boolean | undefined][]) {
        if (value) {
            addClass(header, headerClassMap[key]);
        } else if (value === false) {
            removeClass(header, headerClassMap[key]);
        }
    }

    updateHeaderState();
}

function getHeaderClasses() {
    return Object.keys(headerClassMap).reduce((acc, key) => {
        acc[key as keyof HeaderClasses] = hasClass(header, headerClassMap[key as keyof HeaderClasses]);
        return acc;
    }, {} as HeaderClasses);
}

function removeClass(element: HTMLElement | null, className: string | string[]) {
    if (!element) return;

    if (Array.isArray(className)) {
        className.forEach((c) => element?.classList.remove(c));
    } else {
        element?.classList.remove(className);
    }
}

/**
 * Ovewrites the default setting which controls whther the header can be transparent or not
 */
function setHeaderTransparency(value: boolean) {
    isTransparencyEnabled.value = value;
    setHeaderClasses({ transparent: value });

    if (value) {
        addClass(body, BODY_TRANSPARENT_CLASS);
    } else {
        removeClass(body, BODY_TRANSPARENT_CLASS);
    }
}

const initAnalytics = () => {
    const logo = header.getElementsByClassName("header-logo__link")[0];
    if (logo) {
        logo.addEventListener("click", () => {
            HeaderAnalytics.pushNavigationClick("HP");
        });
    }

    const quickNavItems = header.querySelectorAll<HTMLElement>(".quick-nav__item");
    for (const item of quickNavItems) {
        item.addEventListener("click", (e) => {
            const target = e.currentTarget as HTMLElement;
            if (target.dataset.aText) {
                HeaderAnalytics.pushNavigationClick(target.dataset.aText);
            }
        });
    }
};

export function useHeaderScrollFeatures() {
    isTransparencyEnabled.value = hasClass(body, BODY_TRANSPARENT_CLASS);
    if (!header) {
        return;
    }

    window.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", measureHeader, { passive: true });

    measureHeader();
    scrollHandler();
    nextTick(initAnalytics);

    updateHeaderState();
}

function getHeaderState(): HeaderState {
    const headerClasses = getHeaderClasses();

    return {
        active: isHeaderActivePrivate.value,
        transparent: headerClasses.transparent || false,
        locked: isHeaderDisabled,
        parallax: headerClasses.parallax || false,
        dark: headerClasses.dark || false,
    };
}

function updateHeaderState() {
    const state = getHeaderState();
    headerState.value = state;
}

function setHeaderState(options: HeaderOptions = {}) {
    if (options.animated !== undefined) {
        setHeaderClasses({ animating: options.animated });
    }
    if (options.transparent !== undefined) {
        setHeaderClasses({ transparent: options.transparent });
    }
    if (options.active !== undefined) {
        toggleHeader(options.active);
    }
    if (options.locked !== undefined) {
        options.locked ? lockHeader() : unlockHeader();
    }
    if (options.parallax !== undefined) {
        setHeaderClasses({ parallax: options.parallax });
    }
    if (options.dark !== undefined) {
        setHeaderClasses({ dark: options.dark });
    }
}

function toggleHeader(value = true) {
    value ? showHeader() : hideHeader();
}

function showHeader() {
    if (isHeaderActivePrivate.value) return;

    const animated = getHeaderClasses().animating;

    animated ? showAnimated() : showInstant();
}

function hideHeader() {
    const animated = getHeaderClasses().animating;

    animated ? hideAnimated() : hideInstant();
}

function showCommon() {
    mode = "show";

    if (sazkaNotification) {
        removeClass(sazkaNotification, "sazka-notification--at-top");
    }

    clearShowTimeout();
    clearTransparencyTimeout();
    handleTabletSmartBanner(getPageScrollY());
}

function showInstant(disableTransparency = false) {
    showCommon();
    isHeaderActivePrivate.value = true;
    addClass(body, BODY_ACTIVE_CLASS);

    setHeaderClasses({
        animating: false,
        parallax: false,
        active: true,
        transparent: disableTransparency ? false : undefined,
    });
}

function showAnimated(disableTransparency = false) {
    showCommon();

    const pageScrollY = getPageScrollY();
    if (hasClass(header, PARALLAX_CLASS) && pageScrollY >= 10) {
        setHeaderClasses({
            animating: false,
            parallax: false,
            active: false,
            transparent: disableTransparency ? false : undefined,
        });
        setTimeout(() => {
            isHeaderActivePrivate.value = true;
            addClass(body, BODY_ACTIVE_CLASS);
            setHeaderClasses({ active: true, animating: true });
        }, 50);
    } else {
        isHeaderActivePrivate.value = true;
        addClass(body, BODY_ACTIVE_CLASS);
        setHeaderClasses({
            parallax: false,
            animating: true,
            active: true,
            transparent: disableTransparency ? false : undefined,
        });
    }
}

function showDelayed(disableTransparency = false) {
    if (showTimeout) {
        return;
    }
    showTimeout = window.setTimeout(() => {
        showAnimated(disableTransparency);
        clearShowTimeout();
    }, SHOW_DELAY - 50);
}

function hideCommon() {
    mode = "hide";
    setHeaderClasses({ active: false });
    isHeaderActivePrivate.value = false;
    removeClass(body, BODY_ACTIVE_CLASS);
    clearShowTimeout();
    clearTransparencyTimeout();
}

function hideInstant() {
    setHeaderClasses({ animating: false });
    hideCommon();
}

function hideAnimated() {
    setHeaderClasses({ animating: true });
    hideCommon();
}

function transparency(value = true) {
    setHeaderClasses({ transparent: value });
}

function lockHeader() {
    isHeaderDisabled = true;
}

function unlockHeader() {
    isHeaderDisabled = false;
}

function disableHeader() {
    lockHeader();
    hideAnimated();
    setHeaderState({ parallax: false });
}

function enableHeader() {
    unlockHeader();
    scrollHandler();
}

function resetHeader() {
    enableHeader();
}

function parallax() {
    clearShowTimeout();
    clearTransparencyTimeout();
    if (mode !== "parallax") {
        hideAnimated();
        mode = "parallax";
        setHeaderClasses({ parallax: true, transparent: isTransparencyEnabled.value });
    }
}

function downHandler() {
    if (mode === "parallax") {
        parallax();
    } else if (mode !== "hide") {
        hideAnimated();
    }
}

function clearShowTimeout() {
    if (showTimeout) {
        clearTimeout(showTimeout);
        showTimeout = null;
    }
}

function clearTransparencyTimeout() {
    if (transparencyTimeout) {
        clearTimeout(transparencyTimeout);
        transparencyTimeout = null;
    }
}

function transparencyDelayed() {
    transparencyTimeout = window.setTimeout(() => transparency(), 300);
}

function handleTabletSmartBanner(pageScrollY: number) {
    const { hasSmartbanner, hasNotification, smartBannerHeight, notificationHeight } = useSmartBannerPosition();

    if (hasSmartbanner && window.matchMedia("(min-width: 768px)").matches) {
        if (pageScrollY <= 0 + notificationHeight + smartBannerHeight) {
            addClass(body, "open-header-tablet");
            addClass(html, "has-smartbanner--at-top");
            if (hasNotification) {
                addClass(sazkaNotification, "sazka-notification--at-top");
            }
        } else {
            removeClass(body, "open-header-tablet");
            removeClass(html, "has-smartbanner--at-top");
            if (hasNotification) {
                removeClass(sazkaNotification, "sazka-notification--at-top");
            }
        }
    }
}

function handleSmartbanner(pageScrollY: number) {
    const { hasNotification, smartBannerHeight, notificationHeight } = useSmartBannerPosition();

    if (pageScrollY >= smartBannerHeight + notificationHeight) {
        removeClass(html, "has-smartbanner--at-top");
        if (hasNotification) {
            removeClass(sazkaNotification, "sazka-notification--at-top");
        }
    } else {
        addClass(html, "has-smartbanner--at-top");
        if (hasNotification) {
            addClass(sazkaNotification, "sazka-notification--at-top");
        }
    }
}

function isMenuOpen() {
    const mainMenu = document.getElementById("main-menu");
    const userMenuBtn = document.getElementById("userMenuBtn") as HTMLInputElement | null;

    return mainMenu?.classList.contains("main-menu--active") || userMenuBtn?.checked;
}

function scrollHandler() {
    if (isHeaderDisabled) {
        return;
    }

    const pageScrollY = getPageScrollY();
    const direction = pageScrollY > lastPos ? "down" : "up";
    const isGameOpen = hasClass(body, "casino-open");
    const { hasSmartbanner, smartBannerHeight, notificationHeight } = useSmartBannerPosition();

    if (isMenuOpen()) {
        showAnimated(true);
        lastPos = Math.max(pageScrollY, 0);

        return;
    }

    if (mode !== "show" && pageScrollY <= headerHeight) {
        parallax();
    } else if (direction === "down" && !isGameOpen) {
        downHandler();
    } else if (
        pageScrollY <= 0 + notificationHeight + smartBannerHeight &&
        (isTransparencyEnabled.value || hasSmartbanner)
    ) {
        parallax();

        if (isTransparencyEnabled.value) {
            transparencyDelayed();
        }
    } else if (mode !== "show" && direction === "up") {
        showDelayed(true);
    }

    // Special case for tablet with SmartBanner
    if (hasSmartbanner) {
        removeClass(body, "open-header-tablet");
    }

    // Special case for notification when smartBanner is present
    if (hasSmartbanner) {
        handleSmartbanner(pageScrollY);
    }

    lastPos = Math.max(pageScrollY, 0);
}

function measureHeader() {
    headerHeight = header.offsetHeight;
}
